import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { MapPin, Mail, Phone } from "lucide-react";

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pb-24">
      <Toaster richColors position="top-right" />
      <section className="border-b border-ink/10 py-20" aria-labelledby="contact-title">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("contact.eyebrow")}</div>
          <h1 id="contact-title" className="font-display font-black text-ink text-5xl sm:text-6xl leading-[1]">
            {t("contact.title_a")} <span className="text-teal">{t("contact.title_b")}</span>.
          </h1>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <div>
              <div className="tag mb-2">{t("contact.address_eyebrow")}</div>
              <p className="text-ink/80 flex items-start gap-3">
                <MapPin className="w-5 h-5 text-teal mt-0.5" aria-hidden="true" />
                Semicon Keralam (IIITM-K), Technopark Phase 1, Thiruvananthapuram, Kerala 695584
              </p>
            </div>
            <div>
              <div className="tag mb-2">{t("contact.email_eyebrow")}</div>
              <p className="text-ink/80 flex items-center gap-3">
                <Mail className="w-5 h-5 text-teal" aria-hidden="true" />
                <a href="mailto:info@iiitmk.ac.in" className="hover:text-teal">info@iiitmk.ac.in</a>
              </p>
            </div>
            <div>
              <div className="tag mb-2">{t("contact.phone_eyebrow")}</div>
              <p className="text-ink/80 flex items-center gap-3">
                <Phone className="w-5 h-5 text-teal" aria-hidden="true" />
                <a href="tel:+914712700777" className="hover:text-teal">+91-471-2700777</a>
              </p>
            </div>
            <div>
              <div className="tag mb-2">{t("contact.campus_eyebrow")}</div>
              <p className="text-ink/60 text-sm leading-relaxed">{t("contact.campus_body")}</p>
            </div>
          </div>

          <form onSubmit={submit} className="lg:col-span-7 border border-ink/10 bg-surface p-8 space-y-4" data-testid="contact-form" aria-label={t("nav.contact")}>
            <Field label={t("contact.name")}>
              <input data-testid="contact-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm" />
            </Field>
            <Field label={t("contact.email")}>
              <input data-testid="contact-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm" />
            </Field>
            <Field label={t("contact.subject")}>
              <input data-testid="contact-subject" required value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm" />
            </Field>
            <Field label={t("contact.message")}>
              <textarea data-testid="contact-message" required rows={6} value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-base border border-ink/10 focus:border-teal outline-none px-4 py-3 text-ink text-sm resize-none" />
            </Field>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="w-full bg-brand hover:bg-brand-dark disabled:opacity-50 text-ink py-3.5 font-medium"
            >
              {submitting ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-widest text-ink/50 mb-2">{label}</span>
      {children}
    </label>
  );
}
